import { ModalAction, ModalTitle } from '@overlayqa/shared/ui';
import { useRef, useState } from 'react';
import { Refresh01Icon } from '@hugeicons/core-free-icons';
import { useModalA11y } from '@/hooks/useModalA11y';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../lib/auth';
import { projectsApi } from '../../lib/api/projects';
import { capture } from '@/lib/analytics';
import { ModalPortal } from '@/components/shared/ModalPortal';

export interface ResetDemoModalProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function ResetDemoModal({ projectId, isOpen, onClose }: ResetDemoModalProps) {
  const qc = useQueryClient();
  const { getToken } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y(isOpen, dialogRef, onClose);

  const mutation = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      return projectsApi.resetDemo(token, projectId);
    },
    onSuccess: () => {
      capture('demo_reset_clicked', { projectId });
      qc.invalidateQueries({ queryKey: ['tickets', projectId] });
      qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
      onClose();
    },
    onError: (err) => setError((err as Error).message),
  });

  if (!isOpen) return null;
  const overlay = (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reset-demo-modal-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl w-full max-w-md shadow-xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-2 border-b border-gray-100 flex items-center justify-between">
          <ModalTitle icon={Refresh01Icon} id="reset-demo-modal-title" className="oq-dialog__title">Reset the sample project?</ModalTitle>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2"
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 pt-4 pb-5">
          <p className="modal-intro">
            This restores the original demo content. The 14-day timer keeps running. Delete the demo and create a new one if you want a fresh 14-day window.
          </p>
          {error && <p className="mt-2 text-sm text-red-700">{error}</p>}
          <div className="mt-5 flex justify-end gap-3">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={mutation.isPending}><ModalAction kind="cancel">
              Cancel
            </ModalAction></button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
            ><ModalAction kind="retry">
              {mutation.isPending ? 'Resetting...' : 'Reset demo'}
            </ModalAction></button>
          </div>
        </div>
      </div>
    </div>
  );

  return <ModalPortal>{overlay}</ModalPortal>;
}
